// pkg171c — "Sign Up with LawPay" (8am-required logo + wording) for firms without a LawPay account, shown only when the platform has a plan ID (status.signup_available); submitted/approved/declined application state line; the approval lands via the Event URL and the card then shows as connected on refresh — delivered 2026-10-05
// pkg173 — "Reconcile with LawPay now" button + last-reconcile line + reconnect banner when the gateway rejected the stored credentials — delivered 2026-10-03
// pkg172a — "Switch to live mode" visibly disabled while the platform kill switch is off (opacity + cursor); pages_ready note under the slot map — delivered 2026-10-03
// pkg171a — LawPay (8am) connect card for Firm Settings (D-171a/c/e): "Connect with LawPay" button in 8am's required logo+text form, operating / trust / eCheck slot pickers filled ONLY from the accounts 8am returned (filtered by kind + trust flag — the server re-validates), test↔live switch gated by the platform kill switch, refresh + disconnect; reads the ?lawpay= return flag from the OAuth callback — delivered 2026-10-02
//
// Reads ONE endpoint shape: GET /api/lawpay/status (routes/lawpay.js
// statusPayload). Secrets never reach this component — the status payload
// carries account names, ids, kinds, trust flags and public keys only.
//
// Lane B only (client → firm legal fees). Lane A (firm → LawMighty) is the
// Firm Billing page and Stripe. Never conflate.

import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../utils/adminApi';

const SLOTS = [
  { key: 'operating', label: 'Operating account', kind: 'card', trust: false,
    help: 'Earned fees — invoice payments. Card deposits land here.' },
  { key: 'trust',     label: 'Trust (IOLTA) account', kind: 'card', trust: true,
    help: 'Unearned retainers. Card deposits of retainers land here and nowhere else. Leave empty if your LawPay plan has no trust account — retainer charges will be unavailable rather than misrouted.' },
  { key: 'echeck',    label: 'eCheck (ACH) account', kind: 'ach', trust: false,
    help: 'Bank-account payments. Optional.' },
];

function flagMessage(flag, reason) {
  switch (flag) {
    case 'connected': return { kind: 'success', text: 'LawPay connected. Review the account mapping below — it decides where every payment lands.' };
    case 'denied':    return { kind: 'error',   text: 'LawPay connection was cancelled — no access was granted.' };
    case 'error':     return { kind: 'error',   text: `LawPay connection failed${reason ? ` (${reason.replace(/_/g, ' ')})` : ''}. Try again, or contact LawMighty support if it repeats.` };
    default:          return null;
  }
}

export default function LawPayConnectCard() {
  const [status,  setStatus]  = useState(null);
  const [visible, setVisible] = useState(false);
  const [busy,    setBusy]    = useState('');
  const [error,   setError]   = useState('');
  const [notice,  setNotice]  = useState(null);
  const [draft,   setDraft]   = useState({ operating: '', trust: '', echeck: '' });
  const [editMode, setEditMode] = useState('test'); // which mode's map is being edited

  const load = useCallback(async () => {
    try {
      const s = await adminApi.lawpayStatus();
      setStatus(s);
      setVisible(true);
      const m = (s.account_map && s.account_map[s.mode || 'test']) || {};
      setEditMode(s.mode || 'test');
      setDraft({ operating: m.operating || '', trust: m.trust || '', echeck: m.echeck || '' });
    } catch (err) {
      if (err.status === 403) { setVisible(false); return; } // staff without view_billing
      setVisible(true); setError(err.message);
    }
  }, []);

  useEffect(() => {
    load();
    const q = new URLSearchParams(window.location.search);
    const flag = q.get('lawpay');
    if (flag) {
      setNotice(flagMessage(flag, q.get('reason')));
      // Scrub the flag so a reload doesn't re-announce it.
      q.delete('lawpay'); q.delete('reason');
      const rest = q.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
  }, [load]);

  function pickMode(mode) {
    setEditMode(mode);
    const m = (status && status.account_map && status.account_map[mode]) || {};
    setDraft({ operating: m.operating || '', trust: m.trust || '', echeck: m.echeck || '' });
  }

  // pkg171c — 8am's branded sign-up flow; the redirect is one-time and expires in 5 minutes
  async function signup() {
    setBusy('signup'); setError('');
    try {
      const r = await adminApi.lawpaySignupStart();
      if (r && r.redirect_uri) window.location.assign(r.redirect_uri); else throw new Error('LawPay did not return a sign-up link');
    } catch (e) { setError(e.message); setBusy(''); }
  }

  async function connect() {
    setBusy('connect'); setError('');
    try {
      const { url } = await adminApi.lawpayConnectStart();
      window.location.assign(url); // top-level navigation to secure.lawpay.com
    } catch (err) { setError(err.message); setBusy(''); }
  }

  async function saveMap() {
    setBusy('map'); setError(''); setNotice(null);
    try {
      const s = await adminApi.lawpaySaveAccountMap({ mode: editMode, operating: draft.operating || null, trust: draft.trust || null, echeck: draft.echeck || null });
      setStatus(s);
      setNotice({ kind: 'success', text: `${editMode === 'live' ? 'Live' : 'Test'} account mapping saved.` });
    } catch (err) { setError(err.message); }
    finally { setBusy(''); }
  }

  async function refreshAccounts() {
    setBusy('refresh'); setError(''); setNotice(null);
    try {
      const s = await adminApi.lawpayRefreshAccounts();
      setStatus(s);
      setNotice({ kind: 'success', text: `Accounts refreshed from LawPay (${s.accounts.length} found).` });
    } catch (err) { setError(err.message); }
    finally { setBusy(''); }
  }

  async function setMode(mode) {
    const msg = mode === 'live'
      ? 'Switch to LIVE mode? Real cards will be charged and real money will move into the mapped accounts.'
      : 'Switch to TEST mode? Charges will use LawPay test accounts and move no money.';
    if (!window.confirm(msg)) return;
    setBusy('mode'); setError(''); setNotice(null);
    try {
      const s = await adminApi.lawpaySetMode(mode);
      setStatus(s);
      pickMode(mode);
      setNotice({ kind: 'success', text: `LawPay is now in ${mode.toUpperCase()} mode.` });
    } catch (err) { setError(err.message); }
    finally { setBusy(''); }
  }

  async function reconcile() {
    setBusy('reconcile'); setError(''); setNotice(null);
    try {
      const r = await adminApi.lawpayReconcile();
      setStatus(r.status);
      const s = r.summary;
      setNotice({ kind: s.errors && s.errors.length ? 'error' : 'success',
        text: `Checked ${s.transactions} LawPay transaction${s.transactions === 1 ? '' : 's'}: ${s.updated} updated, ${s.landed} landed${s.unmatched ? `, ${s.unmatched} taken outside LawMighty (not landed)` : ''}${s.errors && s.errors.length ? ` — ${s.errors[0]}` : ''}.` });
    } catch (err) { setError(err.message); }
    finally { setBusy(''); }
  }

  async function disconnect() {
    if (!window.confirm('Disconnect LawPay? LawMighty will stop being able to charge or look up payments for this firm until it is connected again. Existing payment records are kept.')) return;
    setBusy('disconnect'); setError(''); setNotice(null);
    try {
      const r = await adminApi.lawpayDisconnect();
      await load();
      setNotice(r.revoked
        ? { kind: 'success', text: 'LawPay disconnected.' }
        : { kind: 'error', text: 'Disconnected in LawMighty, but LawPay did not confirm the revoke. Remove "LawMighty" under LawPay → Settings → Developers → Authorized Applications.' });
    } catch (err) { setError(err.message); }
    finally { setBusy(''); }
  }

  if (!visible) return null;

  const accounts = (status && status.accounts) || [];
  const modeAccounts = accounts.filter(a => a.mode === editMode);
  const btnBase = { display: 'inline-flex', alignItems: 'center', gap: 10, padding: '10px 18px', border: '1px solid #c9ced6', borderRadius: 6, background: '#fff', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: 14, fontWeight: 600, color: '#0b3140' };
  const p = { fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#333' };

  return (
    <>
      <div className="section-head">Client Payments (LawPay)</div>
      <div className="card">
        {notice && <div className={`alert alert-${notice.kind}`}>{notice.text}</div>}
        {error  && <div className="alert alert-error">{error}</div>}

        {status && !status.configured && (
          <p style={p}>
            LawPay isn't enabled on this LawMighty server yet
            {status.missing_config && status.missing_config.length ? ` (missing: ${status.missing_config.join(', ')})` : ''}.
          </p>
        )}

        {status && status.configured && !status.connected && (
          <>
            <p style={{ ...p, marginTop: 0 }}>
              Let clients pay invoices and retainers by card or eCheck through your firm's own LawPay
              account. LawMighty never holds the money — it records each payment on the client's ledger
              and routes retainers to your trust account automatically. You'll sign in to LawPay and
              approve access; this works with the LawPay account you already have.
            </p>
            <button type="button" style={btnBase} disabled={busy === 'connect'} onClick={connect} aria-label="Connect with LawPay">
              <img src="/lawpay-logo.svg" alt="" style={{ height: 22, width: 'auto', display: 'block' }} />
              <span>{busy === 'connect' ? 'Opening LawPay…' : 'Connect with LawPay'}</span>
            </button>
            {/* pkg171c — no LawPay account yet */}
            {status.signup_available && (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #eee' }}>
                {status.signup_application && status.signup_application.status === 'submitted' ? (
                  <p style={{ ...p, margin: 0 }}>
                    <strong>LawPay application submitted</strong> on {new Date(status.signup_application.created_at).toLocaleDateString()}. LawPay reviews new merchant applications; when it's approved your portal connects automatically and we'll email you. Nothing else to do for now.
                  </p>
                ) : (
                  <>
                    <p style={{ ...p, marginTop: 0 }}>
                      Don't have LawPay yet? Apply from here and your portal connects the moment LawPay approves the account.
                      {status.signup_application && status.signup_application.status === 'declined' && <> <span style={{ color: '#B85042' }}>Your previous application was not approved; you can submit a new one.</span></>}
                    </p>
                    <button type="button" style={{ ...btnBase, background: '#fff' }} disabled={busy === 'signup'} onClick={signup} aria-label="Sign Up with LawPay">
                      <img src="/lawpay-logo.svg" alt="" style={{ height: 22, width: 'auto', display: 'block' }} />
                      <span>{busy === 'signup' ? 'Opening LawPay…' : 'Sign Up with LawPay'}</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {status && status.connected && (
          <>
            {status.needs_reconnect && (
              <div className="alert alert-error">
                LawPay is no longer accepting this connection — it was likely removed under LawPay → Settings → Developers → Authorized Applications. Payments are paused until you Disconnect and connect again.
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
              <img src="/lawpay-logo.svg" alt="LawPay" style={{ height: 22, width: 'auto' }} />
              <span style={{ ...p, fontWeight: 600 }}>{status.merchant_name || 'Connected'}</span>
              <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, padding: '2px 8px', borderRadius: 10, background: status.mode === 'live' ? '#e4f3e8' : '#fff3e0', color: status.mode === 'live' ? '#1e6b35' : '#9a4b00' }}>
                {status.mode === 'live' ? 'LIVE' : 'TEST MODE'}
              </span>
              {status.connected_at && <span style={{ ...p, color: '#888', fontSize: 12 }}>connected {new Date(status.connected_at).toLocaleDateString('en-US')}</span>}
            </div>

            <p style={{ ...p, marginTop: 0 }}>
              <strong>Account mapping</strong> — this decides where money lands. Invoice payments always go
              to the Operating account; retainers always go to the Trust account. The lists below contain
              only the accounts LawPay reported for your merchant, filtered so a trust account can never
              be chosen for operating deposits or the reverse.
            </p>

            {status.has_live_accounts && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {['test', 'live'].map(m => (
                  <button key={m} type="button" className={`btn ${editMode === m ? 'btn-primary' : ''}`} onClick={() => pickMode(m)} style={{ padding: '4px 12px', fontSize: 12 }}>
                    {m === 'live' ? 'Live accounts' : 'Test accounts'}
                  </button>
                ))}
              </div>
            )}

            <div className="form-row">
              {SLOTS.map(slot => {
                const opts = modeAccounts.filter(a => a.kind === slot.kind && a.trust === slot.trust);
                return (
                  <div className="form-group" key={slot.key}>
                    <label>{slot.label}</label>
                    <select value={draft[slot.key] || ''} onChange={e => setDraft(d => ({ ...d, [slot.key]: e.target.value }))}>
                      <option value="">— none —</option>
                      {opts.map(a => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
                    </select>
                    <div style={{ ...p, fontSize: 11, color: '#777', marginTop: 4 }}>
                      {opts.length === 0 ? `No ${slot.trust ? 'trust-flagged' : slot.kind === 'ach' ? 'bank' : 'non-trust card'} ${editMode} account was reported by LawPay. ` : ''}{slot.help}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              <button type="button" className="btn btn-primary" disabled={!!busy} onClick={saveMap}>
                {busy === 'map' ? <><span className="spinner" />Saving…</> : `Save ${editMode === 'live' ? 'live' : 'test'} mapping`}
              </button>
              <button type="button" className="btn" disabled={!!busy} onClick={refreshAccounts}>
                {busy === 'refresh' ? <><span className="spinner" />Refreshing…</> : 'Refresh accounts from LawPay'}
              </button>
              <button type="button" className="btn" disabled={!!busy} onClick={reconcile} title="Pulls settled, pending and reversed transactions from LawPay now instead of waiting for the hourly check">
                {busy === 'reconcile' ? <><span className="spinner" />Checking…</> : 'Check for payments now'}
              </button>
              {status.mode === 'test'
                ? <button type="button" className="btn" disabled={!!busy || !status.live_enabled} style={status.live_enabled ? undefined : { opacity: 0.45, cursor: 'not-allowed' }} title={status.live_enabled ? '' : 'Live payments are not yet enabled on LawMighty'} onClick={() => setMode('live')}>Switch to live mode{status.live_enabled ? '' : ' (not yet available)'}</button>
                : <button type="button" className="btn" disabled={!!busy} onClick={() => setMode('test')}>Switch to test mode</button>}
              <button type="button" className="btn" disabled={!!busy} onClick={disconnect} style={{ marginLeft: 'auto', color: '#b00020' }}>
                {busy === 'disconnect' ? <><span className="spinner" />Disconnecting…</> : 'Disconnect'}
              </button>
            </div>

            {status.accounts_refreshed_at && (
              <p style={{ ...p, fontSize: 11, color: '#aaa', marginBottom: 0, marginTop: 10 }}>
                Accounts last refreshed {new Date(status.accounts_refreshed_at).toLocaleString('en-US')}.
                {status.last_reconcile_at ? ` LawPay last checked ${new Date(status.last_reconcile_at).toLocaleString('en-US')} (automatic every hour).` : ' LawPay not checked yet — runs automatically every hour.'}
                {!status.ready.operating && ' Operating slot is empty — card payments are unavailable until it is set.'}
                {status.pages_ready && status.ready.operating && !status.pages_ready.operating && ' No hosted payment page found for the Operating account — emailed LawPay links are unavailable until LawPay provides one.'}
              </p>
            )}
          </>
        )}
      </div>
    </>
  );
}
